/** Dokumente tab — list (file icon + name + size + date), klik PDF preview.
 *  Z prototype/detail.jsx DetailAltB Dokumente sekce. */

import { useState } from "react";
import { Ic } from "@/components/ui/icons";
import { Empty } from "@/components/ui/empty";
import { Modal } from "@/components/ui/modal";
import { authedFetch } from "@/lib/auth";
import { EDGE_FUNCTION_BASE, supabase } from "@/lib/supabase";
import type { DealDocument } from "@/lib/fetchers";
import { t } from "@/i18n/de";

interface PdfLinkResponse {
  url: string;
  mime_type: string;
  file_name: string;
  expires_in_seconds: number;
}

export function DocumentsTab({ docs, loading }: { docs: DealDocument[]; loading: boolean }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>("");
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openDoc = async (doc: DealDocument) => {
    setOpening(doc.id);
    setError(null);
    try {
      let url: string | null = null;
      if (doc.source === "storage" && doc.storage_path && doc.storage_bucket) {
        // Direct storage signed URL (deal-photos bucket — RLS gated dle deals).
        const { data, error: signErr } = await supabase.storage
          .from(doc.storage_bucket)
          .createSignedUrl(doc.storage_path, 60 * 10);
        if (signErr || !data) throw new Error(signErr?.message || "sign failed");
        url = data.signedUrl;
      } else if (doc.document_id) {
        // Edge fn pdf-link (RLS gated documents)
        const resp = await authedFetch<PdfLinkResponse>(
          `${EDGE_FUNCTION_BASE}/partner-portal/pdf-link`,
          { method: "POST", body: JSON.stringify({ document_id: doc.document_id }) },
        );
        url = resp.url;
      }
      if (!url) throw new Error("no source for document");
      setPreviewUrl(url);
      setPreviewName(doc.label_de || doc.file_name);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpening(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>{t.loading}</div>
    );
  }

  if (docs.length === 0) {
    return <Empty title="Keine Dokumente vorhanden." />;
  }

  return (
    <>
      <div className="card" style={{ overflow: "hidden" }}>
        {docs.map((doc, idx) => (
          <button
            key={doc.id}
            type="button"
            onClick={() => void openDoc(doc)}
            disabled={opening === doc.id}
            style={{
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              borderBottom: idx === docs.length - 1 ? "none" : "1px solid var(--border-subtle)",
              cursor: "pointer",
              width: "100%",
              background: "transparent",
              border: 0,
              borderRadius: 0,
              textAlign: "left",
              color: "var(--text)",
              transition: "background .12s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-row-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {opening === doc.id ? (
              <Ic.Loader size={16} className="animate-spin" />
            ) : (
              <Ic.File size={16} />
            )}
            <span
              style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>
                {doc.label_de}
              </span>
              <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>{doc.file_name}</span>
            </span>
            <div className="num" style={{ fontSize: 11.5, color: "var(--text-3)" }}>
              {doc.size ? formatSize(doc.size) : "—"}
            </div>
            <div
              className="num"
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                width: 80,
                textAlign: "right",
              }}
            >
              {formatShort(doc.created_at)}
            </div>
          </button>
        ))}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: 10,
            fontSize: 12.5,
            color: "var(--red)",
            background: "var(--red-12)",
            border: "1px solid rgba(248,113,113,0.22)",
            borderRadius: "var(--r-md)",
          }}
        >
          {error}
        </div>
      )}

      <Modal open={!!previewUrl} onOpenChange={(o) => !o && setPreviewUrl(null)} width={1000}>
        {previewUrl && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 500 }}>{previewName}</span>
              <div style={{ display: "flex", gap: 8 }}>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary btn-sm"
                  download
                >
                  <Ic.ArrowUpRight size={12} /> Herunterladen
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewUrl(null)}
                  className="btn btn-ghost btn-sm"
                  style={{ padding: 6 }}
                >
                  <Ic.Close size={16} />
                </button>
              </div>
            </div>
            <iframe
              src={previewUrl}
              title={previewName}
              style={{
                width: "100%",
                height: "75vh",
                border: "1px solid var(--border)",
                borderRadius: 8,
                background: "var(--bg-base)",
              }}
            />
          </div>
        )}
      </Modal>
    </>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatShort(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.`;
}
